import React, { useState } from 'react';

const tabs = ['Business Model', 'Process Methodology', 'Quality Assurance', 'Development Infrastructure'];

const businessModelItems = [
  {
    icon: '🌐',
    title: 'Onshore / Offshore Model',
    desc: 'EGB operates a proven Onshore/Offshore delivery model that combines the benefits of local presence with the cost advantages of offshore development. Our USA-based team manages client relationships, requirements, and project oversight, while our India development center handles execution — delivering maximum value at optimized costs.',
  },
  {
    icon: '🤝',
    title: 'Client-Centric Approach',
    desc: 'We place our clients at the center of everything we do. From initial consultation to final delivery, our teams work closely with clients to understand their unique business needs, ensuring solutions are tailored, scalable, and aligned with their strategic goals.',
  },
  {
    icon: '💡',
    title: 'End-to-End Solutions',
    desc: 'EGB offers comprehensive end-to-end solutions spanning Software Development, IT Consulting, Website Design, Internet Marketing, and Back Office Support. Our integrated approach ensures seamless delivery across all phases of a project lifecycle.',
  },
  {
    icon: '🏭',
    title: 'Dedicated Development Centers',
    desc: 'Our state-of-the-art development centers in the USA and India operate 24/7 with redundant systems, secure VPN access, and the latest development tools. This infrastructure enables us to support clients across time zones with uninterrupted service delivery.',
  },
  {
    icon: '📊',
    title: 'Multi-Domain Expertise',
    desc: 'EGB serves diverse industry verticals including Banking, Healthcare, Telecom, Education, Hospitality, Retail, and Government. Our cross-domain expertise allows us to bring best practices from one industry to solve challenges in another.',
  },
  {
    icon: '🔒',
    title: 'Quality & Compliance',
    desc: 'As an ISO 9001:2000 certified organization and GSA contract holder, EGB adheres to the highest standards of quality and compliance. Our structured QA processes, regular audits, and commitment to continuous improvement ensure reliable, secure, and compliant deliverables.',
  },
];

const Strategy = () => {
  const [activeTab, setActiveTab] = useState('Business Model');

  return (
    <>
      <section className="py-5 text-white text-center" style={{ background: 'linear-gradient(135deg, #002868, #1a56db)' }}>
        <div className="container py-4">
          <h1 className="fw-bold">Our Strategy</h1>
          <p className="lead" style={{ color: '#cbd5e1' }}>How we deliver excellence</p>
        </div>
      </section>

      {/* Tabs */}
      <div className="bg-white border-bottom sticky-top" style={{ top: 56, zIndex: 100 }}>
        <div className="container">
          <div className="d-flex overflow-auto" style={{ gap: 0 }}>
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="btn btn-link text-decoration-none px-4 py-3 fw-semibold border-0 rounded-0"
                style={{
                  color: activeTab === tab ? '#1a56db' : '#64748b',
                  borderBottom: activeTab === tab ? '3px solid #1a56db' : '3px solid transparent',
                  whiteSpace: 'nowrap',
                  fontSize: 15,
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="py-5 bg-light">
        <div className="container py-4">

          {/* Business Model */}
          {activeTab === 'Business Model' && (
            <>
              <div className="text-center mb-5">
                <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 mb-2">OUR APPROACH</span>
                <h2 className="fw-bold">Business Model</h2>
                <p className="text-muted mx-auto" style={{ maxWidth: 650 }}>
                  EGB Systems & Solutions operates on a flexible, client-focused business model designed to deliver high-quality technology solutions efficiently and cost-effectively across global markets.
                </p>
              </div>

              {/* Highlight banner */}
              <div className="rounded-4 text-white p-4 mb-5 d-flex align-items-center gap-4" style={{ background: 'linear-gradient(135deg, #002868, #1a56db)' }}>
                <div style={{ fontSize: 56, flexShrink: 0 }}>🏆</div>
                <div>
                  <h5 className="fw-bold mb-1">ISO 9001:2000 Certified · GSA Contract Holder · INC 500 #213</h5>
                  <p className="mb-0" style={{ color: '#cbd5e1' }}>Trusted by Fortune 500 companies across Banking, Healthcare, Telecom, Government, and more.</p>
                </div>
              </div>

              <div className="row g-4">
                {businessModelItems.map((item, i) => (
                  <div key={i} className="col-12 col-md-6">
                    <div className="card border-0 shadow-sm h-100 p-4 rounded-4">
                      <div className="d-flex align-items-start gap-3">
                        <div style={{ fontSize: 40, flexShrink: 0 }}>{item.icon}</div>
                        <div>
                          <h5 className="fw-bold text-primary mb-2">{item.title}</h5>
                          <p className="text-muted mb-0" style={{ lineHeight: 1.8 }}>{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Stats row */}
              <div className="row g-3 mt-4">
                {[
                  { value: '25+', label: 'Years of Experience' },
                  { value: '500+', label: 'Clients Served' },
                  { value: '2000+', label: 'Projects Delivered' },
                  { value: '2', label: 'Global Offices' },
                ].map((stat, i) => (
                  <div key={i} className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm text-center p-4 rounded-4">
                      <div className="fw-bold mb-1" style={{ fontSize: 36, color: '#1a56db' }}>{stat.value}</div>
                      <div className="text-muted small">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Process Methodology */}
          {activeTab === 'Process Methodology' && (
            <>
              <div className="text-center mb-5">
                <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 mb-2">HOW WE WORK</span>
                <h2 className="fw-bold">Process Methodology</h2>
                <p className="text-muted">We follow Agile and Scrum methodologies with iterative development cycles, continuous feedback loops, and transparent communication to ensure projects are delivered on time and within budget.</p>
              </div>
              <div className="card border-0 shadow-sm p-4 rounded-4">
                <p className="text-muted" style={{ lineHeight: 1.8 }}>Our process methodology is built around iterative development, continuous client collaboration, and rigorous quality checkpoints at every stage. We combine the best of Agile, Scrum, and traditional project management to deliver predictable, high-quality outcomes.</p>
              </div>
            </>
          )}

          {/* Quality Assurance */}
          {activeTab === 'Quality Assurance' && (
            <>
              <div className="text-center mb-5">
                <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 mb-2">QUALITY FIRST</span>
                <h2 className="fw-bold">Quality Assurance</h2>
                <p className="text-muted">Our ISO 9001:2000 certified QA process includes rigorous testing at every stage — ensuring zero-defect delivery.</p>
              </div>
              <div className="card border-0 shadow-sm p-4 rounded-4">
                <p className="text-muted" style={{ lineHeight: 1.8 }}>Our QA process includes unit testing, integration testing, UAT, and performance testing at every stage of development, ensuring reliable and secure deliverables.</p>
              </div>
            </>
          )}

          {/* Development Infrastructure */}
          {activeTab === 'Development Infrastructure' && (
            <>
              <div className="text-center mb-5">
                <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 mb-2">INFRASTRUCTURE</span>
                <h2 className="fw-bold">Development Infrastructure</h2>
              </div>
              <div className="card border-0 shadow-sm p-4 rounded-4">
                <p className="text-muted" style={{ lineHeight: 1.8 }}>EGB's quality policy reflects the Organization's commitment to providing solutions of value to its clients. State-of-the-art in technique, our Quality Control methodology combines industry standards and established procedures to create best-of-class software products.</p>
                <p className="text-muted" style={{ lineHeight: 1.8 }}>A Quality Management team comprising professionals with over 10 years of experience in Software Quality Assurance ensures that the software development processes employed and products developed are in conformity with standards.</p>
                <p className="text-muted" style={{ lineHeight: 1.8 }}>We are ISO 9001:2000-certified, and in the process of achieving CMMI standard. We ensure that our Quality Control methodologies are in compliance with the established standards though regular process audits, which enable us to constantly improve and implement more effective software development processes and deliver only reliable technology.</p>

                <div className="row g-4 mt-2">
                  <div className="col-12 col-md-6">
                    <div className="p-3 rounded-3" style={{ background: '#f0f7ff' }}>
                      <h6 className="fw-bold text-primary mb-3">Processes In EGB's Quality Assurance Methodology</h6>
                      <ul className="text-muted mb-0" style={{ lineHeight: 2 }}>
                        <li>Software Design, Development and Testing</li>
                        <li>Creating Plan for Preventive and Corrective Action</li>
                        <li>Process Monitoring and Constant Enhancement</li>
                        <li>Configuration Management</li>
                        <li>Employee Training</li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="p-3 rounded-3" style={{ background: '#f0fdf4' }}>
                      <h6 className="fw-bold text-success mb-3">Capabilities Key to EGB's High-Quality Project Implementation</h6>
                      <ul className="text-muted mb-0" style={{ lineHeight: 2 }}>
                        <li>Effective Project Management</li>
                        <li>Reliable Process Methodology Producing Perfect Product-Requirement Alignment</li>
                        <li>Complete Documentation</li>
                        <li>Excellent Communication</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

        </div>
      </section>
    </>
  );
};

export default Strategy;
